/**
 * TakeBackModal — OPEN-157 B: take a started job back from its driver.
 *
 * POST /v1/jobs/:id/take-back { toDriverId | null, reason (10+) }. DISPATCH_MANAGER and up
 * (TAKE_BACK_ROLES; the server is the gate). The job keeps its status and its run ticket and
 * goes to the chosen driver, or to Unassigned. The old driver's clock is stopped, and anything
 * their phone still sends for the job is kept for the office on the Sync page, not applied.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { JOB_DETAIL_QUERY_KEY } from './use-job-detail';
import { TAKE_BACK_REASON_MIN, UNASSIGNED, takeBackErrorMessage } from './take-back';

interface TakeBackBody {
  toDriverId: string | null;
  reason: string;
}

export interface TakeBackModalProps {
  readonly jobId: string | null;
  readonly haulId: string | null;
  readonly driverId: string | null;
  readonly driverName: string | null;
  readonly drivers: ReadonlyArray<{ readonly driverId: string; readonly driverName: string }>;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onTakenBack: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

export function TakeBackModal({
  jobId,
  haulId,
  driverId,
  driverName,
  drivers,
  open,
  onClose,
  onTakenBack,
}: TakeBackModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [toDriver, setToDriver] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setToDriver('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const mutation = useApiMutation<TakeBackBody, { takebackId: string }>(
    apiOpts,
    `/v1/jobs/${jobId ?? ''}/take-back`,
    'POST',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        if (jobId) void queryClient.invalidateQueries({ queryKey: JOB_DETAIL_QUERY_KEY(jobId) });
        toast.success('Job taken back');
        onTakenBack();
      },
      onError: (err: unknown) => {
        setError(takeBackErrorMessage(err));
      },
    },
  );

  const options = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...drivers
      .filter((d) => d.driverId !== driverId)
      .map((d) => ({ value: d.driverId, label: d.driverName })),
  ];
  const reasonValid = reason.trim().length >= TAKE_BACK_REASON_MIN;
  const canSubmit = toDriver !== '' && reasonValid && !mutation.isPending && jobId !== null;

  const handleConfirm = (): void => {
    if (!canSubmit) return;
    setError(null);
    mutation.mutate({
      toDriverId: toDriver === UNASSIGNED ? null : toDriver,
      reason: reason.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="take-back-modal">
        <DialogHeader>
          <DialogTitle>{`Take back job ${haulId ?? ''}`.trim()}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.4 }}>
            {`${driverName ?? 'The driver'}'s clock on this job stops now. The job keeps its status and any load already picked up. Anything their phone still sends for it goes to the Sync page for you to review.`}
          </p>
          <div>
            <label htmlFor="take-back-driver" style={labelStyle}>
              Give it to
            </label>
            <Combobox
              id="take-back-driver"
              value={toDriver}
              onChange={setToDriver}
              options={options}
              placeholder="Choose a driver"
              searchPlaceholder="Search drivers…"
              emptyText="No drivers match."
            />
          </div>
          <div>
            <label htmlFor="take-back-reason" style={labelStyle}>
              Reason
            </label>
            <textarea
              id="take-back-reason"
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              placeholder={`Why is this job being taken back? (min ${TAKE_BACK_REASON_MIN} characters)`}
              style={textareaStyle}
              aria-label="Take back reason"
            />
            {reason.trim().length > 0 && !reasonValid && (
              <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                Reason must be at least {TAKE_BACK_REASON_MIN} characters.
              </span>
            )}
          </div>
        </div>
        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep with driver
          </Button>
          <Button
            type="button"
            data-testid="take-back-confirm"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            {mutation.isPending ? 'Taking back…' : 'Take back'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
